import { ArrowLeft, Camera, Eye, EyeOff, Pencil, Save } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { SNAPSHOT_DESCRIPTION_MAX_LENGTH } from './constants';
import { SnapshotAnnotationWorkspace } from './snapshot-annotation-workspace';
import type { SnapshotAnnotationWorkspaceHandle } from './snapshot-annotation-workspace';
import { SnapshotWindow } from './snapshot-window';

import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { bakeAnnotations } from '@/components/snapshot/annotation';
import type { AnnotationData } from '@/components/snapshot/annotation';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import type { SnapshotBaseVariant, ViewportSnapshot } from '@/visualization';

interface SnapshotCaptureWizardProps {
    snapshot: ViewportSnapshot;
    /** Image variant shown when overlays are enabled. */
    dataUrlWithOverlay: string;
    /** Image variant shown when overlays are disabled. */
    dataUrlNoOverlay: string;
    /** Close without persisting. */
    onClose: () => void;
    /** Persist without annotations. */
    onSave: (useOverlayImage: boolean, description: string) => void;
    /**
     * Persist with annotations. `annotatedDataUrl` is the baked composite for
     * display (null when all annotations were removed); the native captures
     * are never replaced.
     */
    onSaveAnnotated: (
        annotations: AnnotationData[],
        annotatedDataUrl: string | null,
        baseVariant: SnapshotBaseVariant,
        description: string
    ) => void;
}

type SnapshotCaptureStep = 'preview' | 'annotate';

/**
 * Two-step creation wizard for a freshly captured draft, in one persistent
 * fixed window: preview (choose overlay variant, describe) → annotate (draw
 * directly on the image). Back navigation keeps annotations and the
 * description; Save persists from either step.
 */
export function SnapshotCaptureWizard({
    snapshot,
    dataUrlWithOverlay,
    dataUrlNoOverlay,
    onClose,
    onSave,
    onSaveAnnotated,
}: SnapshotCaptureWizardProps) {
    const [step, setStep] = useState<SnapshotCaptureStep>('preview');
    const [overlayVisible, setOverlayVisible] = useState(true);
    const [description, setDescription] = useState(snapshot.description ?? '');
    const [annotations, setAnnotations] = useState<AnnotationData[]>([]);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

    const workspaceRef = useRef<SnapshotAnnotationWorkspaceHandle | null>(null);

    // Single-variant snapshots (e.g. template captures) have nothing to toggle.
    const hasOverlayVariants = dataUrlWithOverlay !== dataUrlNoOverlay;
    const currentImageUrl = overlayVisible ? dataUrlWithOverlay : dataUrlNoOverlay;
    const baseVariant: SnapshotBaseVariant = overlayVisible ? 'overlay' : 'no-overlay';

    const isDirty = annotations.length > 0 || description.trim() !== (snapshot.description ?? '').trim();

    // Preview step shows annotations made in the annotate step (baked for display).
    const [previewBakedUrl, setPreviewBakedUrl] = useState<string | null>(null);
    useEffect(() => {
        if (step !== 'preview' || annotations.length === 0) {
            setPreviewBakedUrl(null);
            return;
        }
        let cancelled = false;
        void bakeAnnotations(currentImageUrl, annotations).then((baked) => {
            if (!cancelled) setPreviewBakedUrl(baked);
        });
        return () => {
            cancelled = true;
        };
    }, [step, annotations, currentImageUrl]);

    const requestClose = () => {
        if (isDirty) {
            setConfirmDiscardOpen(true);
        } else {
            onClose();
        }
    };

    const handleBack = () => {
        // Commit any in-place text edit and capture the latest annotations
        // before the workspace unmounts.
        const latest = workspaceRef.current?.getAnnotations();
        if (latest) setAnnotations(latest);
        setStep('preview');
    };

    const handleSave = async () => {
        if (step === 'annotate' && workspaceRef.current) {
            const { annotations: latest, compositedDataUrl } = workspaceRef.current.bake();
            onSaveAnnotated(latest, compositedDataUrl, baseVariant, description);
            return;
        }
        if (annotations.length > 0) {
            // Annotations from a previous visit to the annotate step: bake them
            // headlessly onto the currently selected variant.
            const baked = await bakeAnnotations(currentImageUrl, annotations);
            onSaveAnnotated(annotations, baked, baseVariant, description);
            return;
        }
        onSave(overlayVisible, description);
    };

    const overlaysToggle = (
        <Button
            variant="secondary"
            size="sm"
            className="gap-1.5 text-xs shadow-md"
            disabled={!hasOverlayVariants}
            onClick={() => setOverlayVisible((v) => !v)}
        >
            {overlayVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Overlays
        </Button>
    );

    // Cancelling is the window's ✕ (which asks the same discard confirmation).
    const headerActions = (
        <>
            {step === 'preview' ? (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => setStep('annotate')}
                >
                    <Pencil className="h-3.5 w-3.5" />
                    Annotate
                </Button>
            ) : (
                <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleBack}>
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back
                </Button>
            )}
            <Button variant="default" size="sm" className="gap-1.5 text-xs" onClick={() => void handleSave()}>
                <Save className="h-3.5 w-3.5" />
                Save
            </Button>
            <Separator orientation="vertical" className="h-6" />
        </>
    );

    return (
        <SnapshotWindow
            icon={Camera}
            title="New screenshot"
            headerActions={headerActions}
            onClose={requestClose}
            closeDisabled={confirmDiscardOpen}
        >
            {step === 'preview' ? (
                <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted/30 p-3">
                    <img
                        src={previewBakedUrl ?? currentImageUrl}
                        alt="Screenshot preview"
                        className="h-full w-auto max-w-full rounded object-contain shadow-md"
                    />
                    <div className="absolute right-3 top-3 flex items-center gap-1.5">{overlaysToggle}</div>
                </div>
            ) : (
                <SnapshotAnnotationWorkspace
                    ref={workspaceRef}
                    imageUrl={currentImageUrl}
                    initialAnnotations={annotations}
                    onAnnotationsChange={setAnnotations}
                    sidebarExtras={
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start gap-2 text-xs"
                            disabled={!hasOverlayVariants}
                            onClick={() => setOverlayVisible((v) => !v)}
                        >
                            {overlayVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            Overlays
                        </Button>
                    }
                />
            )}

            {/* Description — editable on both steps */}
            <div className="shrink-0 border-t border-border px-3 py-2">
                <div className="flex items-center justify-between pb-1">
                    <label className="text-xs font-medium text-muted-foreground">Description</label>
                    <span className="text-xs text-muted-foreground">
                        {description.length}/{SNAPSHOT_DESCRIPTION_MAX_LENGTH}
                    </span>
                </div>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={SNAPSHOT_DESCRIPTION_MAX_LENGTH}
                    placeholder="Add a description to this snapshot…"
                    rows={2}
                    className="block w-full resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                />
            </div>

            <ConfirmationDialog
                open={confirmDiscardOpen}
                onOpenChange={setConfirmDiscardOpen}
                title="Discard screenshot?"
                description="The capture and any annotations will be lost."
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                variant="destructive"
                onConfirm={onClose}
            />
        </SnapshotWindow>
    );
}
